import { Button, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

export type UnsavedChangesBarProps = {
  /** How many changes are unsaved, said first and in bold. */
  summary: string;
  /** Said after the summary: what saving would apply to, or where the changes are. */
  detail: string;
  discardLabel?: string;
  disabled: boolean;
  onDiscard: () => Promisable<void>;
  onSave: () => Promisable<void>;
};

/**
 * Says that changes are unsaved, and offers to save or discard
 * them. Stays in view at the foot of the page while the rows above scroll.
 */
export const UnsavedChangesBar = ({
  summary,
  detail,
  discardLabel,
  disabled,
  onDiscard,
  onSave,
}: UnsavedChangesBarProps) => {
  const { t } = useTranslation();

  return (
    <Paper
      role="region"
      aria-label={summary}
      elevation={6}
      sx={{
        position: "sticky",
        bottom: 16,
        zIndex: "appBar",
        px: 2.5,
        py: 1.5,
        bgcolor: "primary.dark",
        color: "primary.contrastText",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Typography variant="body2" aria-live="polite" aria-atomic>
          <strong>{summary}</strong> {detail}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            color="inherit"
            disabled={disabled}
            onClick={() => {
              void onDiscard();
            }}
          >
            {discardLabel ?? t("admin.configuration.settings.unsaved.discard")}
          </Button>
          <Button
            variant="contained"
            disabled={disabled}
            onClick={() => {
              void onSave();
            }}
            sx={{
              bgcolor: "background.paper",
              color: "primary.dark",
              "&:hover": { bgcolor: "background.subtle" },
            }}
          >
            {t("admin.configuration.settings.unsaved.save")}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
